import React, { ReactNode, useEffect, useState } from "react";
import { connect } from "react-redux";
import WebConfig from "middlewares/WebConfig";
import { setCoreLayoutHeaderVisibility, setCoreLayoutSidebarVisibility } from "actions/layout";

interface OpenWindowExtraParams {
  searchParams?: SearchParams[],
  features?: WindowFeatures,
  openInNewTab?: boolean,
  sendDataToWindow?: any, //I think any works better for this than Object, using Object gives typescript errors for things that work like an array of strings
  hideSidebar?: boolean,
  hideHeader?: boolean,
  onWindowClosed?: () => void,
}

export interface WindowProviderValue {
  /** @description Used to open a new window with SSO to an ME route */
  openNewWindowWithSSO: (targetPath: string, extras?: OpenWindowExtraParams) => Window;
  windowData: any;
}

export const WindowContext = React.createContext<WindowProviderValue>(null);

interface Props {
  children: ReactNode,
  ssoToken: string,
  hideHeader: () => void,
  hideSidebar: () => void,
}

type StringBool = "yes" | "no";

export interface WindowFeatures {
  width?: number,
  height?: number,
  resizable?: StringBool,
  scrollbars?: StringBool,
  menubar?: StringBool,
  toolbar?: StringBool,
  location?: StringBool,
  status?: StringBool,
}

const defaultWindowFeatures: WindowFeatures = {
  width: 800,
  height: 600,
  resizable: "yes",
  scrollbars: "yes",
  menubar: "no",
  toolbar: "no",
  location: "no",
  status: "no",
};

interface SearchParams {
  key: string,
  value: string,
}

const getBaseUrl = () => {
  const isLocal = window.location.hostname === "localhost" && window.location.port === "3000";

  return {
    isLocal,
    url: isLocal ? window.location.origin : WebConfig.orthosRoot,
  };
};

const MessageTypes = {
  PopoutReady: "popout-ready",
  SetWindowData: "set-window-data",
  HideSidebar: "hide-sidebar",
  HideHeader: "hide-header",
};

// The provider can allow sending a message to the new window once its ready, but to talk to the parent window
// you most likely need to use something like a broadcast channel. If we are going to continually talk back and forth
// in the future, might be worth trying to add something like that here eventually
const WindowProvider = ({ children, ssoToken, hideHeader, hideSidebar }: Props) => {
  const [windowData, setWindowData] = useState<Object>();

  useEffect(() => {
    // Check URL params for immediate hide flags (set by openNewWindowWithSSO)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('hideHeader') === 'true') hideHeader();
    if (urlParams.get('hideSidebar') === 'true') hideSidebar();

    const openerOrigin = getBaseUrl().url;

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: MessageTypes.PopoutReady }, openerOrigin);

      window.addEventListener("message", ev => {
        if (ev.origin !== window.location.origin) return;
        const messageType = ev.data?.type;
        
        if (messageType === MessageTypes.SetWindowData) setWindowData(ev.data?.payload);
        if (messageType === MessageTypes.HideHeader) hideHeader();
        if (messageType === MessageTypes.HideSidebar) hideSidebar();
      });
    }
  }, []);

  const openNewWindowWithSSO = (targetPath: string, { searchParams, features, openInNewTab, sendDataToWindow, hideHeader, hideSidebar, onWindowClosed }: OpenWindowExtraParams) => {
    const base = getBaseUrl();
    let ssoPath = base.isLocal ? 'sso' : 'webui/sso';
    const ssoUrl = new URL(ssoPath, base.url);
    ssoUrl.searchParams.set('token', ssoToken);

    const redirectUrl = new URL(targetPath, base.url);
    if (searchParams) searchParams.forEach(sp => redirectUrl.searchParams.set(sp.key, sp.value));
    // Add hide flags to redirect URL so they're applied immediately on load
    if (hideHeader) redirectUrl.searchParams.set('hideHeader', 'true');
    if (hideSidebar) redirectUrl.searchParams.set('hideSidebar', 'true');

    if (targetPath) ssoUrl.searchParams.set("redirect", redirectUrl.pathname + redirectUrl.search);

    features = { ...defaultWindowFeatures, ...(features ?? {}) };
    let featuresString = Object.keys(features).map(key => `${key}=${features[key]}`).join(",");

    if (openInNewTab) featuresString = "";

    const win = window.open(ssoUrl.toString(), "_blank", featuresString);

    if (sendDataToWindow || hideHeader ||hideSidebar) {
      const messageCallback = (ev: MessageEvent) => {
        if (ev.origin !== window.location.origin) return;

        if (ev.data?.type === MessageTypes.PopoutReady) {
          if (sendDataToWindow) win.postMessage({ type: MessageTypes.SetWindowData, payload: sendDataToWindow });
          if (hideHeader) win.postMessage({ type: MessageTypes.HideHeader });
          if (hideSidebar) win.postMessage({ type: MessageTypes.HideSidebar });
          window.removeEventListener("message", messageCallback);
        }
      };

      window.addEventListener("message", messageCallback);
    }

    if (onWindowClosed) {
      // i cant believe theres no reliable way to see if a window has been closed
      const checkWindowClosed = setInterval(() => {
        if (!win || win.closed) {
          clearInterval(checkWindowClosed);
          onWindowClosed();
        }
      }, 500);
    }

    return win;
  };

  const providerValue: WindowProviderValue = {
    openNewWindowWithSSO,
    windowData
  };

  return (
    <WindowContext.Provider value={providerValue}>
      {children}
    </WindowContext.Provider>
  );
};

const mapStateToProps = state => ({
  ssoToken: state.user.profile.token,
});

const mapDispatchToProps = dispatch => ({
  hideHeader: () => dispatch(setCoreLayoutHeaderVisibility(false)),
  hideSidebar: () => dispatch(setCoreLayoutSidebarVisibility(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WindowProvider);
